import { BaseSyntheticEvent, useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';

import {
  Theme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
} from '@mui/material';

import FormTextField from './formFields/FormTextField';
import Loader from './Loader';
import { findVendorAccount } from '../api/vendorApi/vendorAccountsApi';
import { VendorStatusMap } from '../views/FirmBankingView/constants';
import useSnackBar from '../hooks/useSnackBar';


const useStyles = makeStyles()((theme: Theme) => ({
  popupContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
      minHeight: 'auto',
      padding: '32px',
      borderRadius: '16px',

      '&::-webkit-scrollbar': {
        width: '5px',
        borderRadius: '20px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 20,
        backgroundColor: theme.palette.secondary.dark,
      },
    },

    '& .MuiDialogContentText-root': {
      color: theme.palette.secondary.main,
    },
  },
  popupActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: 0,
    marginTop: '32px',

    '@media (max-width: 550px)': {
      flexDirection: 'column',
      gap: '16px',

      '@media (max-width: 550px)': {
        paddingRight: '16px',
      },
    },
  },
  popupSelectButton: {
    color: theme.palette.common.white,
    background: theme.palette.info.main,
    maxWidth: '200px',
    width: '100%',
    borderRadius: '12px',
    height: '48px',

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.08)',
      background: 'rgba(255, 255, 255, 0.08)',
    },

    '&:hover': {
      background: theme.palette.info.main,
    },

    '@media (max-width: 550px)': {
      maxWidth: '100%',
    },
  },
  popupCancelButton: {
    color: theme.palette.common.white,
    background: theme.palette.info.dark,
    maxWidth: '200px',
    borderRadius: '12px',
    width: '100%',
    height: '48px',

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.08)',
      background: 'rgba(255, 255, 255, 0.08)',
    },

    '&:hover': {
      background: theme.palette.info.dark,
    },

    '@media (max-width: 550px)': {
      maxWidth: '100%',
    },
  },
  popupContent: {
    overflowY: 'hidden',
    marginTop: '32px',
    padding: 0,
    flex: 'none',
  },
  noResultsText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  noResultsHeadline: {
    fontSize: '20px',
    lineHeight: '41px',
    fontWeight: 600,
    fontFamily: 'Inter',
    textAlign: 'center',

    '@media (max-width: 600px)': {
      fontSize: '16px',
    },
  },
  searchResultsBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '300px',
    overflowY: 'auto',

    '@media (max-width: 550px)': {
      paddingRight: '10px',
    },

    '&::-webkit-scrollbar': {
      width: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  resultItemContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    padding: '10px',
    borderRadius: '5px',
    gap: '50px',

    '@media (max-width: 550px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },

    '&:hover': {
      cursor: 'pointer',
      backgroundColor: 'rgba(255, 255, 255, 0.19)',
    },
  },
  resultItemColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '33%',
    minWidth: 0,
    overflowWrap: 'break-word',
  },
  resultItemColumnLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#93959E',
    fontFamily: 'Inter',
  },
  resultItemColumnText: {
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'Inter',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
  },
  resultItemActiveBgColor: {
    backgroundColor: 'rgba(255, 255, 255, 0.19)',

    '@media (max-width: 550px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      flexBasis: '33%',
      minWidth: 0,
    },
  },
  dialogTitle: {
    padding: 0,
    fontSize: '20px',
    lineHeight: '41px',
    fontWeight: 600,
    fontFamily: 'Inter',
    textAlign: 'center',
    marginBottom: '32px',

    '@media (max-width: 600px)': {
      fontSize: '16px',
    },
  },
  formWrapper: {
    '@media (max-width: 550px)': {
      paddingRight: '16px',
    },
  },
  existingVendorChip: {
    height: '20px',
    borderRadius: '10px',
    border: `1px solid ${VendorStatusMap['EXISTING']?.color }`,
    background: `${VendorStatusMap['EXISTING']?.color }19`,
    '& .MuiChip-label': {
      fontFamily: 'Inter',
      fontSize: '11px',
      fontWeight: '500',
      color: VendorStatusMap['EXISTING']?.color,
      padding: '2px 6px',
    },
  },
  existingVendorContainer: {
    cursor: 'not-allowed !important',
    opacity: 0.5,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
      cursor: 'not-allowed !important',
    },
  },
  selectableVendorContainer: {
    cursor: 'pointer',
  },
}));

interface VendorData {
  id: number;
  tin: string | null;
  name: string;
  address1: string;
  address2: string;
  city: string;
  countryState: string;
  zip: string;
  createdAt: string;
  updatedAt: string;
  hasFirmParticipation?: boolean;
}

interface SearchVendorPopupProps {
  open: boolean;
  onClose: () => void;
  onSelectVendor?: (vendor: VendorData) => void;
}

const SearchVendorPopup = ({
  open,
  onClose,
  onSelectVendor,
}: SearchVendorPopupProps) => {
  const styles = useStyles();
  const { setAlert } = useSnackBar();

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<VendorData[]>([]);
  const [selectedResultItem, setSelectedResultItem] = useState<VendorData | null>(null);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      searchQuery: '',
    },
  });

  const { reset, setValue } = formMethods;

  // Fetch all vendors when popup opens or search is cleared
  const fetchAllVendors = async () => {
    setIsDataLoading(true);
    try {
      const response = await findVendorAccount();
      setSearchResult(response.data.vendors);
    } catch (error) {
      setAlert(() => ({        
        message: 'Failed to load vendor accounts.',
        type: 'error',
      }));
      setSearchResult([]);
    } finally {
      setIsDataLoading(false);
    }
  };

  // Fetch vendors with search parameter
  const fetchVendorsWithSearch = async (searchValue: string) => {
    setIsDataLoading(true);
    try {
      const response = await findVendorAccount(searchValue);
      setSearchResult(response.data.vendors);
    } catch (error) {
      setAlert(() => ({        
        message: 'Failed to search vendor accounts.',
        type: 'error',
      }));
      setSearchResult([]);
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleSearchVendor = (value: string) => {
    setSearchInputValue(value);
    setValue('searchQuery', value);
    handleResetPopup();

    if (value.length > 2) {
      // Make API call with search value
      fetchVendorsWithSearch(value);
    } else if (value.length === 0) {
      // If search is cleared, show all vendors again
      fetchAllVendors();
    } else {
      // For 1-2 characters, clear results but don't make API call
      setSearchResult([]);
    }
  };

  const handleScroll = (event: BaseSyntheticEvent) => {
    const listboxNode = event.currentTarget;

    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    if (listboxNode.scrollHeight - position <= 1) {
      // Pagination can be implemented here if needed
    }
  };

  const handleSelectResultItem = (item: VendorData) => {
    setSelectedResultItem(item);
  };

  const handleResetPopup = () => {
    setSearchResult([]);
    setSelectedResultItem(null);
  };

  const handleClosePopup = () => {
    reset();
    handleResetPopup();
    onClose();
  };

  const onSelectResultItem = () => {
    if (!selectedResultItem) return;

    if (onSelectVendor) {
      onSelectVendor(selectedResultItem);
    }
    reset();
    handleResetPopup();
    onClose();
  };

  // Load all vendors when popup opens
  useEffect(() => {
    if (open) {
      fetchAllVendors();
    } else {
      // Reset when popup closes
      handleResetPopup();
      reset();
    }
  }, [open]);

  return (
    <Dialog
      className={styles.classes.popupContainer}
      open={open}
      onClose={handleClosePopup}
      fullWidth
    >
      <DialogTitle className={styles.classes.dialogTitle}>
        Search Vendor by TIN or Vendor Name
      </DialogTitle>
      <Box className={styles.classes.formWrapper}>
        <FormProvider {...formMethods}>
          <form style={{ width: '100%' }}>
            <FormTextField name="searchQuery" label="Search" handleChange={handleSearchVendor} />
          </form>
        </FormProvider>
      </Box>

      <DialogContent
        className={clsx(
          styles.classes.popupContent,
          !searchResult.length && styles.classes.noResultsText,
        )}
      >
        {isDataLoading && !searchResult.length ? (
          <Box position="absolute" top="52%" left="45%">
            <Loader size={50} colorType="info" />
          </Box>
        ) : !isDataLoading && searchResult.length === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography className={styles.classes.noResultsHeadline}>
              {searchInputValue.length > 0 && searchInputValue.length <= 2
                ? 'Type at least 3 symbols to search.'
                : searchInputValue.length > 2
                  ? 'No vendors found matching your search.'
                  : 'No vendors available.'}
            </Typography>
          </Box>
        ) : (
          <Box className={styles.classes.searchResultsBlock} onScroll={handleScroll}>
            {searchResult.map((resultItem) => (
              <Box
                onClick={() => resultItem.hasFirmParticipation !== true && handleSelectResultItem(resultItem)}
                className={clsx(
                  styles.classes.resultItemContainer,
                  resultItem.id === selectedResultItem?.id &&
                  styles.classes.resultItemActiveBgColor,
                  resultItem.hasFirmParticipation === true
                    ? styles.classes.existingVendorContainer
                    : styles.classes.selectableVendorContainer,
                )}
                key={resultItem.id}
              >
                <Box className={styles.classes.resultItemColumn}>
                  <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
                    <Typography className={styles.classes.resultItemColumnLabel}>
                      Name
                    </Typography>
                    {resultItem.hasFirmParticipation === true && (
                      <Chip
                        label={VendorStatusMap['EXISTING']?.label}
                        variant="outlined"
                        className={styles.classes.existingVendorChip}
                      />
                    )}
                  </Box>
                  <Typography className={styles.classes.resultItemColumnText}>
                    {resultItem.name}
                  </Typography>
                </Box>
                <Box className={styles.classes.resultItemColumn}>
                  <Typography className={styles.classes.resultItemColumnLabel}>
                    EIN/TIN
                  </Typography>
                  <Typography className={styles.classes.resultItemColumnText}>
                    {resultItem.tin ?? '—'}
                  </Typography>
                </Box>
                <Box className={styles.classes.resultItemColumn}>
                  <Typography className={styles.classes.resultItemColumnLabel}>
                    Address
                  </Typography>
                  <Typography className={styles.classes.resultItemColumnText}>
                    {`${resultItem.address1}${resultItem.address2 ? `, ${resultItem.address2}` : ''}, ${resultItem.city}, ${resultItem.countryState} ${resultItem.zip}`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions className={styles.classes.popupActions}>
        <Button
          className={styles.classes.popupSelectButton}
          onClick={onSelectResultItem}
          disabled={!selectedResultItem}
        >
          Select
        </Button>
        <Button className={styles.classes.popupCancelButton} onClick={handleClosePopup}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SearchVendorPopup;


